import { inject, Injectable } from '@angular/core';
import { UserMetaService } from '@app/client';
import { map, Observable, of, switchMap } from 'rxjs';
import { getUpline, UserRoles, UserRolesLevels } from '../enums/roles.enum';
import { ISelectItem } from '../interfaces/select-item.interface';

export const DASHBOARD_COMMERCIAL_ROLES = [
  UserRoles.IS_STORE_MANAGER,
  UserRoles.IS_JUNIOR_MANAGER,
  UserRoles.IS_AGENTE_JUNIOR,
  UserRoles.IS_AGENTE_NEW,
  UserRoles.IS_AGENTE_PROVA,
  UserRoles.IS_AGENTE_SENIOR,
  UserRoles.IS_REGIONAL_MANAGER,
  UserRoles.IS_AREA_MANAGER,
];

@Injectable()
export class UserSharedService {
  userMetaService = inject(UserMetaService);

  calculateNetworkPriority(userRoles: UserRoles[]): UserRoles[] {
    let maxLevel = 0;
    userRoles?.forEach((r) => {
      const level = UserRolesLevels.indexOf(<UserRoles>r);
      if (maxLevel < level) {
        maxLevel = level;
      }
    });

    return UserRolesLevels.slice(maxLevel + 1);
  }

  getNetworkDownlineList(
    bypassNetworkByRoles: string[],
    filterByRoles?: string[],
    rootUserId?: string,
    excludeUsersWithNoNetwork?: boolean,
    includeInactive?: boolean,
  ): Observable<ISelectItem[]> {
    return this.userMetaService
      .getUsersListDownlineFromFusion(bypassNetworkByRoles, filterByRoles, rootUserId, includeInactive)
      .pipe(
        map((res) => {
          if (excludeUsersWithNoNetwork) {
            res = res.filter((r: any) => r.children && r.children[0]);
          }
          return res.map((r) => ({
            value: r.id,
            label: `${r.firstName} ${r.lastName}${r.active === false ? ' (Disattivato)' : ''}`,
          }));
        }),
      );
  }

  resolveDashboardUsers(networkId?: string | null, siteId?: string | null, area?: number | null): Observable<ISelectItem[]> {
    const root = networkId || undefined;
    return this.getNetworkDownlineList(root ? [] : [UserRoles.IS_ADMIN], DASHBOARD_COMMERCIAL_ROLES, root, false, true).pipe(
      switchMap((users) => {
        const sorted = [...users].sort((a, b) => a.label.localeCompare(b.label));
        if (siteId) {
          return this.userMetaService.getUsersBySite(siteId).pipe(map((metas) => this.keepListed(sorted, metas.map((m) => m.user))));
        }
        if (area != null) {
          return this.userMetaService.getUsersByArea(area).pipe(map((metas) => this.keepListed(sorted, metas.map((m) => m.user))));
        }
        return of(sorted);
      }),
    );
  }

  private keepListed(users: ISelectItem[], ids: string[]) {
    const allowed = new Set(ids);
    return users.filter((user) => allowed.has(user.value));
  }

  getOperatorList(): Observable<ISelectItem[]> {
    return this.getNetworkDownlineList([UserRoles.IS_ADMIN, UserRoles.IS_BACKOFFICE_NETWORK_OVERRIDER], [], undefined, false, true);
  }

  getNetworkUplineList() {
    return this.getNetworkDownlineList(
      [UserRoles.IS_ADMIN, UserRoles.IS_BACK_OFFICE],
      getUpline(UserRoles.IS_AGENTE_SENIOR),
      undefined,
      true,
      true,
    );
  }

  getAllAgentsList(): Observable<ISelectItem[]> {
    return this.getNetworkDownlineList(
      [UserRoles.IS_ADMIN, UserRoles.IS_BACK_OFFICE, UserRoles.IS_PROPERTY_FINDER],
      getUpline(UserRoles.IS_AGENTE_PROVA),
      undefined,
      false,
      true,
    );
  }

  getAllAgentsAndBackofficeList(): Observable<ISelectItem[]> {
    return this.getNetworkDownlineList(
      [UserRoles.IS_ADMIN, UserRoles.IS_BACK_OFFICE, UserRoles.IS_PROPERTY_FINDER],
      [...getUpline(UserRoles.IS_AGENTE_PROVA), UserRoles.IS_BACK_OFFICE],
      undefined,
      false,
      true,
    );
  }
}
